import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useUpdateGuardianProfileMutation } from '@/features/guardian/queries';
import { useAvatarPicker } from '@/hooks/use-avatar-picker';
import { uploadImageToStorage } from '@/libs/firebase/storage/storage-helpers';
import { useSessionStore, useUiStore } from '@/stores';
import { shouldUploadAvatarUri } from '@/utils/helper';
import { getCountryName, resolveAddressLocation } from '@/utils/address-location';
import { MAX_AVATAR_FILE_SIZE } from '@/validation';
import { useShallow } from 'zustand/shallow';

import type { GuardianProfileEditForm } from '../types';
import { useGuardianProfile } from './use-guardian-profile';

type UseGuardianProfileEditOptions = { onSuccess?: () => void };

export function useGuardianProfileEdit({ onSuccess }: UseGuardianProfileEditOptions = {}) {
  const uid = useSessionStore(state => state.user?.uid);
  const { data: profile, avatarUri } = useGuardianProfile();
  const updateProfile = useUpdateGuardianProfileMutation(uid);
  const pickAvatar = useAvatarPicker();
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(state => ({
      showGlobalLoading: state.showGlobalLoading,
      hideGlobalLoading: state.hideGlobalLoading,
    })),
  );

  const defaultValues = useMemo<GuardianProfileEditForm>(() => {
    const location = resolveAddressLocation(profile?.address?.country, profile?.address?.state);

    return {
      avatar: avatarUri ? { uri: avatarUri } : null,
      firstName: profile?.firstname ?? '',
      lastName: profile?.lastname ?? '',
      phone: profile?.phone ?? '',
      email: profile?.email ?? '',
      country: location.country,
      state: location.state,
      city: profile?.address?.city ?? '',
      zipCode: profile?.address?.zipcode ?? '',
    };
  }, [avatarUri, profile]);
  const form = useForm<GuardianProfileEditForm>({ defaultValues, mode: 'onChange' });

  useEffect(() => {
    if (profile) form.reset(defaultValues);
  }, [defaultValues, form, profile]);

  const handlePickAvatar = useCallback(() => {
    pickAvatar(value => {
      if (value.fileSize && value.fileSize > MAX_AVATAR_FILE_SIZE) {
        showAlert({
          title: STRINGS.common.alert.error,
          message: STRINGS.guardianProfile.edit.avatarTooLarge,
        });
        return;
      }
      form.setValue('avatar', value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  }, [form, pickAvatar]);

  const submit = form.handleSubmit(async values => {
    try {
      showGlobalLoading();
      let photoUrl = values.avatar?.uri;
      if (uid && shouldUploadAvatarUri(photoUrl)) {
        const uploaded = await uploadImageToStorage(
          photoUrl,
          `profile-pictures/guardian/${uid}.jpg`,
        );
        photoUrl = `${uploaded.downloadUrl}&v=${Date.now()}`;
      }
      await updateProfile.mutateAsync({
        firstname: values.firstName.trim(),
        lastname: values.lastName.trim(),
        phone: values.phone.trim(),
        photoUrl,
        address: {
          ...profile?.address,
          country: getCountryName(values.country),
          state: values.state.trim() || null,
          city: values.city.trim() || null,
          zipcode: values.zipCode.trim() || null,
        },
      });
      form.reset(values);
      showAlert({
        title: STRINGS.common.alert.success,
        message: STRINGS.guardianProfile.edit.updateSuccess,
        buttons: [{ text: STRINGS.dismiss, onPress: onSuccess }],
      });
    } finally {
      hideGlobalLoading();
    }
  });

  return { form, submit, handlePickAvatar };
}
